'use client';

import { useTranslation } from 'next-i18next';
import { AnimatedCollapsible, Button } from '@components/common';
import { HeroIconType } from '@type/icons';
import {
  BanknotesIcon, HeartIcon as HeartIconOutline, BookOpenIcon, BriefcaseIcon, SunIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { commonStyles } from '../common-styles';

// Interface
// ************************************
export interface EmployerBenefitsProps {
  benefits?: ApiSchema<'GroupedStaticItem'>[];
}

// Icons
// ************************************
export const BenefitGroupsIcon: { [key: string]: HeroIconType } = {
  1: BanknotesIcon,
  2: HeartIconOutline,
  3: BookOpenIcon,
  4: BriefcaseIcon,
  5: SunIcon,
};

/**
 * @description Employer Benefits component. Renders employer benefits.
 * Displays a list of benefit groups and their associated benefits.
 *
 * - **benefits** - ApiSchema<'GroupedStaticItem'>[].
 */
export const EmployerBenefits = (props: EmployerBenefitsProps) => {
  // Destructure props
  const {
    benefits,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Do not render if no benefits
  if (!benefits?.length) {
    return null;
  }

  // Render component
  // *****************************************
  return (
    <div className="space-y-4">
      <h2 className={commonStyles.title}>
        {t('company.detail.benefits')}
      </h2>
      <div>
        <AnimatedCollapsible
          renderContent={
            () => (
              <div className="space-y-6 text-sm">
                {benefits.map((benefitsGroup) => {
                  if (!benefitsGroup.childList?.length) {
                    return null;
                  }

                  const Icon = benefitsGroup.id && BenefitGroupsIcon[benefitsGroup.id] ? BenefitGroupsIcon[benefitsGroup.id] : null;

                  return (
                    <div key={benefitsGroup.id}>
                      <div className="mb-2.5 flex items-center space-x-2 font-bold">
                        {Icon && <Icon className="mt-0.5 size-5 shrink-0" />}
                        <span>{benefitsGroup.name}</span>
                      </div>
                      <div className="space-y-2">
                        {benefitsGroup.childList.map((benefit) => (
                          <div key={benefit.id} className="flex">
                            <CheckCircleIcon className="mr-2 mt-0.5 size-4 shrink-0 text-ink-medium" />
                            {benefit.name}
                          </div>
                        )) }
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          }
          renderToggle={(isExpanded, handleToggle) => (
            <>
              {!isExpanded && (
                <div className={commonStyles.readMoreGradient} />
              )}
              <div className="mt-1 flex justify-center">
                <Button size="sm" color="white" rounding="full" onClick={handleToggle} className={commonStyles.showMoreButton}>
                  {isExpanded ? t('global.label.see_less') : t('global.label.see_more')}
                </Button>
              </div>
            </>
          )}
          collapsedHeight={8 * 24} // line-height is 24px and we display max. 8 rows
        />
      </div>
    </div>
  );
};
